import { useState } from "react"
import { changedValues, type FormInput, InputGroups } from "../../ui/InputField.tsx"
import { Modal } from "../../ui/Modal.tsx"
import { setStrategyRange, type StrategyInstance, updateStrategy } from "../workspace/workspaceStore.ts"
import { RANGE_FIELDS, rangeOf, rangeProblem, rangeValues } from "./backtestRange.ts"
import { useFxCurrencies } from "./fxRates.ts"
import { accountCurrencyField, propertyFieldsWith } from "./properties.ts"
import type { StrategyResult } from "./protocol.ts"
import { strategyInfoFor, usePaneBacktest } from "./strategyResults.ts"

type Tab = "inputs" | "properties"

/**
 * TradingView's strategy settings, Inputs and Properties tabs, for a pane's
 * strategy. The inputs and the strategy's own default properties come from its
 * latest run.
 */
export function StrategySettingsDialog({ initialTab = "inputs", instance, onClose, pane, symbolCurrency }: {
  readonly pane: number
  readonly instance: StrategyInstance
  /** The charted symbol's currency: the default account currency. */
  readonly symbolCurrency: string
  readonly initialTab?: Tab
  readonly onClose: () => void
}) {
  // From the strategy's latest successful run, on this or an earlier series.
  const result = strategyInfoFor(usePaneBacktest(pane), instance.id)
  if (result === null) {
    return (
      <Modal title="Strategy settings" onClose={onClose}>
        <p className="muted">The strategy hasn't run yet.</p>
      </Modal>
    )
  }
  return (
    <SettingsForm
      pane={pane}
      instance={instance}
      result={result}
      initialTab={initialTab}
      onClose={onClose}
      symbolCurrency={symbolCurrency}
    />
  )
}

function SettingsForm({ initialTab, instance, onClose, pane, result, symbolCurrency }: {
  readonly pane: number
  readonly instance: StrategyInstance
  readonly result: StrategyResult
  readonly symbolCurrency: string
  readonly initialTab: Tab
  readonly onClose: () => void
}) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const inputDefaults = result.defaultInputs
  // The default account currency is the symbol's, whatever the strategy's script declares.
  const propertyDefaults: Record<string, unknown> = { ...result.defaultProperties, currency: symbolCurrency }
  const [inputs, setInputs] = useState<Record<string, unknown>>({ ...inputDefaults, ...instance.inputs })
  const [properties, setProperties] = useState<Record<string, unknown>>({ ...propertyDefaults, ...instance.properties })
  // Properties › Backtesting range: kept on the pane's strategy, not with the engine's properties.
  const [range, setRange] = useState<Record<string, unknown>>(rangeValues(instance))
  const [problem, setProblem] = useState<string | null>(null)
  const currencies = useFxCurrencies()
  const propertyFields = propertyFieldsWith(accountCurrencyField(symbolCurrency, currencies, String(properties["currency"] ?? symbolCurrency)))
  const isRangeField = (id: string) => RANGE_FIELDS.some((field) => field.id === id)

  return (
    <Modal title={result.title} onClose={onClose}>
      <nav className="dialog-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "inputs"} onClick={() => setTab("inputs")}>Inputs</button>
        <button type="button" role="tab" aria-selected={tab === "properties"} onClick={() => setTab("properties")}>Properties</button>
      </nav>
      <form
        noValidate
        className="settings-form"
        onSubmit={(event) => {
          event.preventDefault()
          const rangeError = rangeProblem(range)
          if (rangeError !== null) {
            setTab("properties")
            setProblem(rangeError)
            return
          }
          updateStrategy(pane, {
            inputs: changedValues(result.inputConfig as ReadonlyArray<FormInput>, inputs, inputDefaults),
            properties: changedValues(propertyFields, properties, propertyDefaults)
          })
          const { from, to } = rangeOf(range)
          setStrategyRange(pane, from, to)
          onClose()
        }}
      >
        {tab === "inputs"
          ? result.inputConfig.length === 0
            ? <p className="muted">This strategy has no inputs.</p>
            : (
              <InputGroups
                inputs={result.inputConfig as ReadonlyArray<FormInput>}
                values={inputs}
                onChange={(id, value) => setInputs((v) => ({ ...v, [id]: value }))}
              />
            )
          : (
            <>
              <InputGroups
                inputs={[...RANGE_FIELDS, ...propertyFields]}
                values={{ ...properties, ...range }}
                onChange={(id, value) => {
                  setProblem(null)
                  if (isRangeField(id)) setRange((v) => ({ ...v, [id]: value }))
                  else setProperties((v) => ({ ...v, [id]: value }))
                }}
              />
              {problem !== null && <p className="down" role="alert">{problem}</p>}
            </>
          )}
        <footer className="settings-actions">
          <button
            type="button"
            className="link"
            onClick={() => {
              if (tab === "inputs") setInputs({ ...inputDefaults })
              else {
                setProperties({ ...propertyDefaults })
                setRange({ rangeFrom: "", rangeTo: "" })
                setProblem(null)
              }
            }}
          >
            Reset to defaults
          </button>
          <span className="toolbar-spacer" />
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary">Ok</button>
        </footer>
      </form>
    </Modal>
  )
}
